import { Loader2 } from "lucide-react";
import { Navigate, useParams } from "react-router";

import { Breadcrumb } from "@/components/breadcrumb";
import { ClientAccessDeniedCard } from "@/components/clients/client-access-denied-card";
import { clientAccessDenialOf } from "@/lib/api/client-access-denial";
import { useStandaloneInstanceSource } from "@/lib/context/standalone-instance-context";
import { useClient } from "@/lib/hooks/use-client-detail";
import { useFlowInstanceMetadata } from "@/lib/hooks/use-flow-instances";
import { useStandaloneMode } from "@/lib/hooks/use-standalone-mode";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./page.i18n";
import { ProcessView } from "./process-view";

export default function ProcessPage() {
  const { id: clientId, instanceId } = useParams();
  // QP-150: sourced from granular reads (metadata + client) instead of the retired
  // monolithic GET /internal/flow-instances/:id.
  const {
    metadata,
    error: metaError,
    isLoading: isMetaLoading,
  } = useFlowInstanceMetadata(instanceId!);
  const { client, error: clientError, isLoading: isClientLoading } = useClient(clientId ?? null);
  const t = useT(T);
  const { standalone } = useStandaloneMode();
  // QL-261 / QL-262 — publish the persisted internalRedirectUrl to the root
  // standalone store so the "return" logic and the navigation guard prefer it.
  useStandaloneInstanceSource(metadata?.internalRedirectUrl);

  const denial = clientAccessDenialOf(metaError) ?? clientAccessDenialOf(clientError);
  if (denial) return <ClientAccessDeniedCard denial={denial} />;

  if (isMetaLoading || isClientLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!metadata || metadata.clientId !== clientId) {
    // QL-262 — in standalone mode the dashboard is out of bounds (the guard
    // would cancel the redirect anyway); say what happened instead.
    if (standalone) {
      return <p className="py-12 text-center text-sm text-muted-foreground">{t.notFound}</p>;
    }
    return <Navigate to="/" replace />;
  }

  // Instance name wins over the flow template name — same display rule as the
  // wizard header (renamed instances must read as themselves everywhere).
  const displayName = metadata.name || metadata.flowName;

  return (
    <div className="space-y-6">
      {/* QL-33 standalone mode: no breadcrumbs out of the flow */}
      {!standalone && (
        <Breadcrumb
          items={[
            { label: t.clients, href: "/clients" },
            { label: client?.name || t.client, href: `/clients/${clientId}` },
            {
              label: displayName || t.process,
              href: `/clients/${clientId}/flows/${instanceId}`,
            },
            { label: t.process },
          ]}
        />
      )}

      <ProcessView
        flowInstanceId={metadata.id}
        flowId={metadata.flowId}
        flowName={displayName || ""}
        clientId={clientId!}
        clientEmail={client?.email || null}
        clientMobilePhone={client?.mobilePhone || null}
      />
    </div>
  );
}
